import { useCallback, useState } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import clsx from 'clsx'

import HorizontalContainerWithFadedOverflow from 'components/Common/HorizontalContainerWithFadedOverflow'
import IconButton from 'LuxKit/Button/IconButton'
import LineAngleLeftIcon from 'LuxKit/Icons/line/LineAngleLeftIcon'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import { mediaQueryUp } from 'components/utils/breakpoint'
import useHorizontalScrollIntoView from 'hooks/useHorizontalScrollIntoView'

const Container = styled.div`
  position: relative;
  background-color: ${(props) => props.theme.palette.neutral.default.eight};
  border-bottom: ${(props) =>
    `1px solid ${props.theme.palette.neutral.default.six}`};
`

const FloatingActionContainer = styled.div`
  display: none;
  position: absolute;
  top: 50%;
  pointer-events: none;
  opacity: 1;
  transition: .2s ease opacity;

  ${mediaQueryUp.tablet} {
    display: block;
  }

  > * {
    pointer-events: initial;
    box-shadow: ${props => props.theme.shadow.bottom.small};
  }

  &.at-start {
    left: 0;
    transform: translateX(-50%) translateY(-50%);
  }

  &.at-end {
    right: 0;
    transform: translateX(50%) translateY(-50%);
  }

  &.is-hidden {
    opacity: 0;

    > * {
      pointer-events: none;
    }
  }
`

interface Props {
  selectedId?: string
  className?: string
  children: Array<React.ReactElement>
}

export function ScrollMenu({
  selectedId,
  children,
  className,
}: Props) {
  const [hasReachedStart, setHasReachedStart] = useState<boolean>(false)
  const [hasReachedEnd, setHasReachedEnd] = useState<boolean>(false)

  const [scrollerRef, childElements] = useHorizontalScrollIntoView(selectedId, { scrollCondition: 'outOfView' })

  const scrollBackward = useCallback(() => {
    if (scrollerRef.current) {
      const width = scrollerRef.current.clientWidth
      scrollerRef.current.scrollBy(-0.75 * width, 0)
    }
  }, [scrollerRef])

  const scrollForward = useCallback(() => {
    if (scrollerRef.current) {
      const width = scrollerRef.current.clientWidth
      scrollerRef.current.scrollBy(0.75 * width, 0)
    }
  }, [scrollerRef])

  return (
    <Container className={className} data-testid="scroll-menu">
      <HorizontalContainerWithFadedOverflow
        onHasReachedStart={setHasReachedStart}
        onHasReachedEnd={setHasReachedEnd}
        ref={scrollerRef}
      >
        {children.map((child) => {
          const { id } = child.props as { id?: string }
          return React.cloneElement(child, {
            // @ts-expect-error The ref prop is valid here but TypeScript doesn't recognize it
            ref: (element: HTMLElement | null) => {
              if (id && element) {
                childElements.current[id] = element
              }
            },
          })
        })}
      </HorizontalContainerWithFadedOverflow>
      <FloatingActionContainer
        className={clsx('at-start', {
          'is-hidden': hasReachedStart,
        })}
      >
        <IconButton
          kind="primary"
          variant="ghost"
          size="small"
          shape="circle"
          onClick={scrollBackward}
        >
          <LineAngleLeftIcon />
        </IconButton>
      </FloatingActionContainer>
      <FloatingActionContainer
        className={clsx('at-end', {
          'is-hidden': hasReachedEnd,
        })}
      >
        <IconButton
          kind="primary"
          size="small"
          variant="ghost"
          shape="circle"
          onClick={scrollForward}
        >
          <LineAngleRightIcon />
        </IconButton>
      </FloatingActionContainer>
    </Container>
  )
}
